import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import JWTDecoder from 'jwt-decode';
import { Grid, Typography, Button, IconButton, Card, CardContent, CardActionArea } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Link from '@material-ui/core/Link';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import FolderIcon from '@material-ui/icons/Folder';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import EditIcon from '@material-ui/icons/Edit';
import LowPriorityIcon from '@material-ui/icons/LowPriority';
import DeleteIcon from '@material-ui/icons/Delete';
import PeopleIcon from '@material-ui/icons/People';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';

import Api from '../../Api';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { NewFolderModal, MoveFolderModal, RenameFileModal, MoveFileModal, UploadDialog, GrantsManager, FileItem } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  breadcrumb: {
    cursor: 'pointer'
  },
  toolbarButton: {
    marginLeft: theme.spacing(1)
  }
}));

const Drive = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const jwt = localStorage.getItem('access-jwt');
  var jwtUser = [];
  if (jwt !== null) {
    jwtUser = JWTDecoder(localStorage.getItem('access-jwt'));
  }
  const isManager = jwtUser.u_role_id !== 3;
  // Only administrators (role 1) may delete in the Drive (files and folders).
  const isAdmin = jwtUser.u_role_id === 1;

  const [allFolders, setAllFolders] = useState([]); // flat list, managers only (for move)
  const [clients, setClients] = useState([]); // managers only (for grants)
  const [currentFolder, setCurrentFolder] = useState(null); // null => root level
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]); // [{id, name}]
  const [viewMode, setViewMode] = useState(localStorage.getItem('drive-view') || 'list'); // 'list' | 'grid'

  const handleViewMode = (event, value) => {
    if (value) {
      setViewMode(value);
      localStorage.setItem('drive-view', value);
    }
  };

  // Modals
  const [newFolderVisibility, setNewFolderVisibility] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [uploadVisibility, setUploadVisibility] = useState(false);
  const [grantsTarget, setGrantsTarget] = useState(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState(null);
  const [renameFileTarget, setRenameFileTarget] = useState(null);
  const [moveFileTarget, setMoveFileTarget] = useState(null);

  const showError = message => {
    enqueueSnackbar(message || 'Error de conexion, contacta a soporte', {
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      variant: 'error'
    });
  };

  const loadRoot = () => {
    setCurrentFolder(null);
    setBreadcrumb([]);
    setFiles([]);
    if (isManager) {
      Api.get('/drive_folders')
        .then(function(response) {
          const all = response.data.data || [];
          setAllFolders(all);
          setSubfolders(all.filter(f => f.parent_id === null || f.parent_id === undefined));
        })
        .catch(function() {
          setAllFolders([]);
          setSubfolders([]);
        });
    } else {
      Api.get('/drive_folders/mine')
        .then(function(response) {
          setSubfolders(response.data.data || []);
        })
        .catch(function() {
          setSubfolders([]);
        });
    }
  };

  const loadContents = folderId => {
    Api.get('/drive_folders/' + folderId + '/contents')
      .then(function(response) {
        const data = response.data.data;
        setCurrentFolder(data);
        setSubfolders(data.children || []);
        setFiles(data.files || []);
      })
      .catch(function() {
        showError('No se pudo abrir la carpeta.');
      });
  };

  const refresh = () => {
    if (currentFolder) {
      loadContents(currentFolder.id);
      if (isManager) {
        // keep the flat list fresh for the move modal
        Api.get('/drive_folders')
          .then(function(response) {
            setAllFolders(response.data.data || []);
          })
          .catch(function() {});
      }
    } else {
      loadRoot();
    }
  };

  useEffect(() => {
    loadRoot();
    if (isManager) {
      Api.get('/clients')
        .then(function(response) {
          setClients(response.data.data || []);
        })
        .catch(function() {
          setClients([]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenFolder = folder => {
    setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
    loadContents(folder.id);
  };

  const handleCrumbClick = index => {
    if (index < 0) {
      loadRoot();
      return;
    }
    const crumb = breadcrumb[index];
    setBreadcrumb(breadcrumb.slice(0, index + 1));
    loadContents(crumb.id);
  };

  // --- Modal close handlers ---
  const handleCloseNewFolder = success => {
    setNewFolderVisibility(false);
    setRenameTarget(null);
    if (success) {
      refresh();
    }
  };

  const handleCloseMove = success => {
    setMoveTarget(null);
    if (success) {
      refresh();
    }
  };

  const handleCloseUpload = success => {
    setUploadVisibility(false);
    if (success) {
      refresh();
    }
  };

  const handleCloseGrants = () => {
    setGrantsTarget(null);
  };

  const handleCloseRenameFile = success => {
    setRenameFileTarget(null);
    if (success) {
      refresh();
    }
  };

  const handleCloseMoveFile = success => {
    setMoveFileTarget(null);
    if (success) {
      refresh();
    }
  };

  const handleConfirmDeleteFolder = () => {
    const folder = confirmDeleteFolder;
    setConfirmDeleteFolder(null);
    Api.delete('/drive_folders/' + folder.id)
      .then(function() {
        enqueueSnackbar('Carpeta eliminada exitosamente!', {
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          variant: 'success'
        });
        refresh();
      })
      .catch(function(error) {
        var message = 'Error de conexion, contacta a soporte';
        if (error.response) {
          message = 'Ocurrio un error, intenta nuevamente.';
        }
        showError(message);
      });
  };

  const renderFolderActions = folder =>
    isManager ? (
      <React.Fragment>
        <Tooltip title="Gestionar accesos">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setGrantsTarget(folder);
            }}
            style={{ color: '#1976d2' }}>
            <PeopleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Renombrar">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setRenameTarget(folder);
            }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mover">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setMoveTarget(folder);
            }}>
            <LowPriorityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isAdmin ? (
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                setConfirmDeleteFolder(folder);
              }}
              style={{ color: '#e91e63' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </React.Fragment>
    ) : null;

  return (
    <div className={classes.root}>
      <Grid container spacing={3} alignItems="center" justify="flex-end">
        <Grid item style={{ textAlign: 'right' }}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={handleViewMode}
            style={{ verticalAlign: 'middle', marginRight: isManager ? 8 : 0 }}>
            <ToggleButton value="list" aria-label="Vista de lista">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="Vista de grilla">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          {isManager ? (
            <React.Fragment>
              <Button className={classes.toolbarButton} onClick={() => setNewFolderVisibility(true)} color="primary" variant="contained" startIcon={<CreateNewFolderIcon />}>
                Nueva Carpeta
              </Button>
              <Tooltip title={currentFolder ? '' : 'Entrá a una carpeta para subir archivos'}>
                <span>
                  <Button
                    className={classes.toolbarButton}
                    onClick={() => setUploadVisibility(true)}
                    color="primary"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    disabled={!currentFolder}>
                    Subir Archivo
                  </Button>
                </span>
              </Tooltip>
            </React.Fragment>
          ) : null}
        </Grid>
      </Grid>

      <div className={classes.content}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color={breadcrumb.length === 0 ? 'textPrimary' : 'inherit'} className={classes.breadcrumb} onClick={() => handleCrumbClick(-1)}>
            Documentos
          </Link>
          {breadcrumb.map((crumb, index) => (
            <Link
              key={crumb.id}
              color={index === breadcrumb.length - 1 ? 'textPrimary' : 'inherit'}
              className={classes.breadcrumb}
              onClick={() => handleCrumbClick(index)}>
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>
      </div>

      <Card className={classes.content}>
        <CardContent>
          {subfolders.length === 0 && files.length === 0 ? (
            <Typography color="textSecondary">Esta ubicación está vacía.</Typography>
          ) : viewMode === 'list' ? (
            <List>
              {subfolders.map(folder => (
                <ListItem key={'folder-' + folder.id} button onClick={() => handleOpenFolder(folder)}>
                  <ListItemIcon>
                    <FolderIcon style={{ color: '#ffb300' }} />
                  </ListItemIcon>
                  <ListItemText primary={folder.name} />
                  {renderFolderActions(folder)}
                </ListItem>
              ))}
              {files.map(file => (
                <FileItem
                  key={'file-' + file.id}
                  file={file}
                  fileDeleted={refresh}
                  isManager={isManager}
                  isAdmin={isAdmin}
                  viewMode="list"
                  onRename={setRenameFileTarget}
                  onMove={setMoveFileTarget}
                />
              ))}
            </List>
          ) : (
            <Grid container spacing={2}>
              {subfolders.map(folder => (
                <Grid item key={'folder-' + folder.id} xs={6} sm={4} md={3} lg={2}>
                  <Card variant="outlined" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardActionArea onClick={() => handleOpenFolder(folder)} style={{ flexGrow: 1, padding: 16, textAlign: 'center' }}>
                      <FolderIcon style={{ color: '#ffb300', fontSize: 48 }} />
                      <Typography variant="body2" noWrap title={folder.name} style={{ marginTop: 8 }}>
                        {folder.name}
                      </Typography>
                    </CardActionArea>
                    {isManager ? <div style={{ textAlign: 'center', paddingBottom: 4 }}>{renderFolderActions(folder)}</div> : null}
                  </Card>
                </Grid>
              ))}
              {files.map(file => (
                <Grid item key={'file-' + file.id} xs={6} sm={4} md={3} lg={2}>
                  <FileItem
                    file={file}
                    fileDeleted={refresh}
                    isManager={isManager}
                    isAdmin={isAdmin}
                    viewMode="grid"
                    onRename={setRenameFileTarget}
                    onMove={setMoveFileTarget}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {isManager ? (
        <React.Fragment>
          <NewFolderModal
            handleClose={handleCloseNewFolder}
            visibility={newFolderVisibility || renameTarget !== null}
            mode={renameTarget ? 'rename' : 'create'}
            parentId={currentFolder ? currentFolder.id : null}
            folder={renameTarget}
          />
          <MoveFolderModal handleClose={handleCloseMove} visibility={moveTarget !== null} folder={moveTarget} allFolders={allFolders} />
          <UploadDialog handleClose={handleCloseUpload} visibility={uploadVisibility} folderId={currentFolder ? currentFolder.id : null} />
          <GrantsManager handleClose={handleCloseGrants} visibility={grantsTarget !== null} folder={grantsTarget} clients={clients} />
          <RenameFileModal handleClose={handleCloseRenameFile} visibility={renameFileTarget !== null} file={renameFileTarget} />
          <MoveFileModal handleClose={handleCloseMoveFile} visibility={moveFileTarget !== null} file={moveFileTarget} allFolders={allFolders} />

          <ConfirmDialog
            handleClose={() => setConfirmDeleteFolder(null)}
            handleOnConfirm={handleConfirmDeleteFolder}
            visibility={confirmDeleteFolder !== null}
            title="¿Está seguro que desea eliminar esta carpeta?"
            message="Se eliminará la carpeta junto con su contenido. Esta acción es permanente."
          />
        </React.Fragment>
      ) : null}
    </div>
  );
};

export default Drive;
